import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin, Navigation, Clock, Send } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Branches = () => {
  const containerRef = useRef(null);

  const branches = [
    {
      name: "Vanasthalipuram (Main)",
      address: "Beside Line Of Vivekanandha Park, Hyderabad, 500070",
      hours: "09:00 AM — 09:00 PM (Everyday)",
      featured: true,
      mapUrl: "https://maps.app.goo.gl/1KD7SS8UeyVASEJq9"
    },
    {
      name: "SR Pet clinic, Hasthinapuram",
      address: "Hasthinapuram Central, Hyderabad",
      hours: "09:30 AM — 09:00 PM",
      featured: false,
      mapUrl: "https://maps.app.goo.gl/AitGp4EtcSMpVQQd9"
    },
    {
      name: "Pet Planet Dog Clinic, Turkayamjal",
      address: "Turkayamjal, Hyderabad",
      hours: "10:00 AM — 08:30 PM",
      featured: false,
      mapUrl: "https://maps.app.goo.gl/AngNqHp4njfe8crS6"
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.branch-reveal', 
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="bg-secondary/20 relative overflow-hidden" id="branches">
      <div className="absolute top-[20%] left-[20%] w-[40vw] h-[40vw] bg-primary/5 blur-[100px] rounded-full pointer-events-none" />
      
      <div className="section-container">
        <div className="text-center space-y-6 mb-16 lg:mb-24 relative z-10">
          <div className="flex items-center justify-center gap-4">
             <span className="w-12 h-px bg-primary/30"></span>
             <span className="branch-reveal text-primary font-bold tracking-[0.2em] uppercase text-[10px] md:text-[11px] block">Global Access</span>
             <span className="w-12 h-px bg-primary/30"></span>
          </div>
          <h2 className="branch-reveal text-navy font-display font-bold leading-tight">
             Our Clinical <br /> <span className="text-primary italic font-normal">Branches.</span>
          </h2>
          <p className="branch-reveal text-luxury max-w-2xl mx-auto mt-6">
            Find a Dr. Suresh Pet Care sanctuary near you, armed with cutting-edge diagnostics and expert staff in every location.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-12 relative z-10">
          {branches.map((branch, i) => (
            <div 
              key={i} 
              className={`branch-reveal p-8 sm:p-10 lg:p-12 rounded-[2.5rem] sm:rounded-[3.5rem] transition-all duration-700 flex flex-col justify-between h-full group border shadow-glass-heavy hover:shadow-2xl hover:-translate-y-2 cursor-pointer overflow-hidden relative ${
                branch.featured 
                ? 'bg-[#010816] border-white/10 text-white' 
                : 'bg-white border-navy/10 text-navy'
              }`}
            >
              {/* Decorative Subtle Background */}
              <div className={`absolute -bottom-20 -left-20 w-48 h-48 rounded-full blur-[60px] transition-all duration-1000 -z-10 ${
                branch.featured ? 'bg-primary/20 group-hover:bg-primary/30' : 'bg-primary/5 group-hover:bg-primary/10'
              }`}></div>
              
              <div>
                <div className="flex justify-between items-start mb-8 sm:mb-10">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-[1.25rem] sm:rounded-[1.5rem] flex items-center justify-center shadow-lg transition-transform duration-700 group-hover:-rotate-3 ${
                    branch.featured ? 'bg-primary text-white' : 'bg-navy text-white'
                  }`}>
                    <MapPin size={24} className="sm:w-7 sm:h-7" />
                  </div>
                  {branch.featured && (
                    <span className="px-4 py-1.5 bg-primary text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg shadow-primary/20">HEADQUARTERS</span>
                  )}
                </div>
                
                <h3 className={`font-display font-bold text-xl sm:text-2xl mb-6 transition-colors ${
                  branch.featured ? 'text-white' : 'text-navy group-hover:text-primary'
                }`}>{branch.name}</h3>
                
                <div className={`space-y-5 mb-10 border-l-2 pl-6 ${
                  branch.featured ? 'border-primary/30' : 'border-navy/5'
                }`}>
                  <div className="flex items-start gap-4">
                     <MapPin size={18} className="text-primary mt-1 shrink-0 opacity-80" />
                     <p className={`text-[12px] sm:text-sm font-bold leading-relaxed uppercase tracking-wide ${
                       branch.featured ? 'text-white/70' : 'text-navy/70'
                     }`}>{branch.address}</p>
                  </div>
                  <div className="flex items-center gap-4">
                     <Clock size={18} className="text-primary shrink-0 opacity-80" />
                     <p className={`text-[12px] sm:text-sm font-black uppercase tracking-widest ${
                       branch.featured ? 'text-white' : 'text-primary'
                     }`}>{branch.hours}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:gap-4 mt-auto">
                <a 
                  href={branch.mapUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`h-11 sm:h-14 rounded-xl sm:rounded-full flex justify-center items-center gap-2 font-bold uppercase tracking-widest text-[10px] sm:text-[11px] transition-all duration-500 border-2 ${
                    branch.featured 
                    ? 'bg-primary border-primary text-white hover:bg-white hover:text-primary hover:border-white shadow-lg shadow-primary/20' 
                    : 'bg-white border-navy/10 text-navy hover:bg-navy hover:text-white'
                  }`}
                >
                  Directions
                  <Navigation size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>

                <a 
                  href={`https://wa.me/919848954921?text=${encodeURIComponent(`Hi Dr. Suresh, I would like to book an appointment for my pet at the ${branch.name} branch.`)}`}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`h-11 sm:h-14 rounded-xl sm:rounded-full flex justify-center items-center gap-2 font-bold uppercase tracking-widest text-[10px] sm:text-[11px] transition-all duration-500 border-2 ${
                    branch.featured 
                    ? 'bg-transparent border-white/20 text-white hover:bg-white hover:text-navy hover:border-white' 
                    : 'bg-primary border-primary text-white hover:bg-navy hover:border-navy shadow-lg shadow-primary/20'
                  }`}
                >
                  Book Now
                  <Send size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Branches;
